"use client";

import { baseCardCls, noBtnCls, shadowCls, yesBtnCls } from "@/components/platform/style";
import VolRect from "@/components/platform/vol-rect";
import { CricketMatchRecordDisplay } from "@/components/sport-event/cricket-board/cricket-match-record-display";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { OrderType } from "@/components/trade-panel/types";
import { SmartImage } from "@/components/ui/smart-image";
import { type DOMInfo, useCollectDOMInfo } from "@/hooks/useCollectDOMInfo";
import { cn } from "@/lib/utils";
import { isEventExpired, isGuessExpired } from "@/utils/expiration";
import { formatCentPriceAsChancePercent } from "@/utils/price";
import { navigateToEventDetail, useAppRouter } from "@/utils/router";
import { formatUnitPrice, get, getOptionLabel } from "@/utils/utils";
import { EventCategory } from "@yn/utils";
import dayjs from "dayjs";
import { usePathname } from "next/navigation";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import PredictionCardContainer from "../sport-event/PredictionCardContainer";
import { resolveSportEventCardState } from "./sport-event-card-state";
import { getSportIcon } from "./sport-icons";

//
// SportEventCard 组件的 ref 类型定义
export interface SportEventCardRef {
    collectDOMInfo: () => DOMInfo;
}

interface SportEventCardProps {
    eventInfo: any;
    className?: string;
    orderType?: OrderType;
    onCardClick?: () => void; // Card click event
}

const DEFAULT_OPTION_PRICE = 50;
type OptionPriceKey = "yesRealtimePrice" | "noRealtimePrice";

function getOptionPrice( guess: any, key: OptionPriceKey ) {
    return get( guess, key, DEFAULT_OPTION_PRICE ) as number | string;
}

function getOptionPercentText( guess: any, key: OptionPriceKey ) {
    return formatCentPriceAsChancePercent( getOptionPrice( guess, key ) );
}

export const SportEventCard = forwardRef<SportEventCardRef, SportEventCardProps>(
    function SportEventCard( { eventInfo, className, orderType, onCardClick }, ref ) {
        const router = useAppRouter();
        const pathname = usePathname();
        const { open } = useTradePanel();
        const [ selectedGuessIndex, setSelectedGuessIndex ] = useState( 0 );
        const [ forceUpdate, setForceUpdate ] = useState( 0 );

        // 使用 DOM 收集 Hook
        const { registerButton, collectDOMInfo } = useCollectDOMInfo();

        // 暴露 DOM 收集方法给父组件
        useImperativeHandle( ref, () => ( { collectDOMInfo } ) );

        const matchup1 = eventInfo.matchups?.[0] || {};
        const matchup2 = eventInfo.matchups?.[1] || {};
        const {
            guess,
            aggregateGuesses,
            aggregateSelectedIndex,
            showAggregatePredictions,
            showMatchupSection,
            isPromotedEvent,
            shouldRender,
        } = useMemo( () => resolveSportEventCardState( eventInfo, selectedGuessIndex ), [
            eventInfo,
            selectedGuessIndex,
        ] );

        // 判断是否为 LIVE 状态
        const now = Date.now();
        const occurTime = get( eventInfo, "occurTime", 0 );
        const endTime = get( eventInfo, "endTime", 0 );
        const isLive = now >= occurTime && now <= endTime;

        // 判断事件是否已结束：检查 endTime 或 acceptEndTime，任一过期即为过期
        const isEnded = isEventExpired( eventInfo ) || isGuessExpired( guess );
        const yesPercentText = getOptionPercentText( guess, "yesRealtimePrice" );
        const noPercentText = getOptionPercentText( guess, "noRealtimePrice" );
        const yesDisplayText = yesPercentText;
        const noDisplayText = noPercentText;
        const eventCategory = Number( get( eventInfo, "eventCategory", EventCategory.Normal ) );
        const isCricketEvent = eventCategory === EventCategory.Cricket || eventInfo.ifCricket === true;
        const sportIcon = getSportIcon( isCricketEvent ? EventCategory.Cricket : eventCategory as EventCategory );
        const shouldRenderSportTitle = isCricketEvent && Boolean( sportIcon );
        const cardTitle = get( eventInfo, "title", "-" );

        const handleGameViewClick = () => {
            if ( onCardClick ) {
                onCardClick();
            } else {
                const eventCategory = get( eventInfo, "eventCategory", undefined );
                const guessNo = !isPromotedEvent ? get( guess, "guessNo", "" ) : undefined;
                void navigateToEventDetail( router, {
                    eventNo: eventInfo.eventNo,
                    isMainPageEvent: true,
                    ...( guessNo && { guessNo } ),
                    ...( eventCategory !== undefined && { eventCategory } ),
                } );
            }
        };

        const handleMatchupClick = ( isYes: boolean ) => {
            open( {
                guessInfo: guess,
                eventInfo,
                selectedOption: isYes ? "yes" : "no",
                tradeType: "buy",
                pathname,
                detailInfo: eventInfo,
                ...( orderType ? { orderType } : {} ),
            } );
        };
        const gameTimeText = useMemo( () => {
            if ( eventInfo.liveState === 1 ) {
                return false;
            } else if ( eventInfo.occurTime > now ) {
                // 未开始
                return dayjs( eventInfo.occurTime ).format( "h:mm A" );
            } else if ( eventInfo.occurTime < now ) {
                return "Final";
            }
            return false;
        }, [ eventInfo ] );

        if ( !shouldRender ) return null;

        return (
            <div className={cn( baseCardCls, shadowCls, className )}>
                {/* 顶部信息栏 */}
                <button
                    type="button"
                    className="flex w-full items-center justify-between mb-3 gap-4 cursor-pointer border-0 bg-transparent p-0 text-left"
                    onClick={handleGameViewClick}
                >
                    {/* 左边区域：LIVE + 时间和交易量 */}
                    {shouldRenderSportTitle
                        ? (
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                                <SmartImage src={sportIcon ?? undefined} alt="" className="size-6 shrink-0" />
                                <span className="min-w-0 flex-1 truncate text-xs font-medium text-t3">{cardTitle}</span>
                            </div>
                        )
                        : <span className="text-t1 text-sm font-medium flex-1 line-clamp-1 break-all">{cardTitle}
                        </span>}
                    <div className="flex gap-2">
                        {gameTimeText
                            ? (
                                <div className="text-xs font-medium text-t1 bg-surface-card-label p-1.5 flex items-center rounded shrink-0">
                                    {gameTimeText}
                                </div>
                            )
                            : null}
                    </div>
                </button>

                {showMatchupSection && (
                    <>
                        {/* 第二行 - 第一个队伍 */}
                        <div
                            className="flex items-center mb-2 gap-2 cursor-pointer w-full"
                            onClick={handleGameViewClick}
                        >
                            {/* 左边：图片 + 队伍名称 + 比分 */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                {/* 队伍图标 */}
                                {matchup1.matchupImageUrl
                                    ? (
                                        <SmartImage
                                            src={matchup1.matchupImageUrl}
                                            alt={matchup1.title}
                                            className="size-8 rounded object-contain shrink-0"
                                            imageUrlOptions={{ width: 150, height: 150 }}
                                        />
                                    )
                                    : (
                                        <div className="flex items-center justify-center w-[50px] h-6 rounded bg-[#265CFF] text-content-fixed-inverse text-xs font-bold shrink-0">
                                            {matchup1.title?.substring( 0, 3 ).toUpperCase() || "TM1"}
                                        </div>
                                    )}
                                {/* 队伍名称 */}
                                <span
                                    className={cn(
                                        "text-sm text-t1 line-clamp-1 break-all min-w-0 flex-1 max-w-full flex items-center gap-1",
                                        isCricketEvent ? "font-medium" : "font-normal",
                                    )}
                                >
                                    {matchup1.title || "Team 1"}

                                    {/* 比分 */}
                                    {matchup1.matchRecord && (
                                        <div className="text-sm font-normal text-t3 flex-[0_0_auto] whitespace-nowrap">
                                            <CricketMatchRecordDisplay
                                                matchRecord={matchup1.matchRecord}
                                                showCompactInnings={isCricketEvent}
                                            />
                                        </div>
                                    )}
                                </span>
                            </div>

                            {/* 右边：百分比 */}
                            <div className={cn( "text-base font-medium text-t1", isEnded && "opacity-40" )}>
                                {yesDisplayText}
                            </div>
                        </div>

                        {/* 第二行 - 第二个队伍 */}
                        <div className="flex items-center mb-4 cursor-pointer gap-2" onClick={handleGameViewClick}>
                            {/* 左边：图片 + 队伍名称 + 比分 */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                {/* 队伍图标 */}
                                {matchup2.matchupImageUrl
                                    ? (
                                        <SmartImage
                                            src={matchup2.matchupImageUrl}
                                            alt={matchup2.title}
                                            className="size-8 rounded object-contain flex-shrink-0"
                                            imageUrlOptions={{ width: 150, height: 150 }}
                                        />
                                    )
                                    : (
                                        <div className="flex items-center justify-center w-[50px] h-6 rounded bg-[#AA00FF] text-content-fixed-inverse text-xs font-bold">
                                            {matchup2.title?.substring( 0, 3 ).toUpperCase() || "TM2"}
                                        </div>
                                    )}
                                {/* 队伍名称 */}
                                <span
                                    className={cn(
                                        "text-sm text-t1 flex-1 line-clamp-1 min-w-0 max-w-full flex items-center gap-1",
                                        isCricketEvent ? "font-medium" : "font-normal",
                                    )}
                                >
                                    {matchup2.title || "Team 2"}

                                    {/* 比分 */}
                                    {matchup2.matchRecord && (
                                        <div className="text-sm font-normal text-t3 flex-[0_0_auto] whitespace-nowrap">
                                            <CricketMatchRecordDisplay
                                                matchRecord={matchup2.matchRecord}
                                                showCompactInnings={isCricketEvent}
                                            />
                                        </div>
                                    )}
                                </span>
                            </div>

                            {/* 右边：百分比 */}
                            <div className={cn( "text-base font-medium text-t1", isEnded && "opacity-40" )}>
                                {noDisplayText}
                            </div>
                        </div>
                    </>
                )}

                {showAggregatePredictions && (
                    <div className="mb-3" onClick={( e ) => e.stopPropagation()}>
                        <div className="h-5 flex items-center gap-6 mb-2 text-t1 text-sm font-medium">
                            <div className="text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0">
                                {get( guess, "groupTitle", "" ) || get( guess, "title", "" )}
                            </div>
                            <div className="text-base font-semibold">{yesPercentText}</div>
                        </div>
                        <PredictionCardContainer
                            sliderValues={aggregateGuesses}
                            selectedIndex={aggregateSelectedIndex}
                            onSliderChange={setSelectedGuessIndex}
                        />
                    </div>
                )}

                {/* 底部按钮 */}
                {!isEnded && (
                    <div className="flex gap-2 mb-3">
                        {/* 左边按钮 (Team 1) */}
                        <div className="flex-1" onClick={( e ) => e.stopPropagation()}>
                            <button
                                ref={( el ) => registerButton( "team1-button", el )}
                                onClick={() => handleMatchupClick( true )}
                                className={cn(
                                    "relative w-full h-10 rounded text-sm font-medium transition-opacity hover:opacity-80 flex items-center gap-1 justify-center",
                                    yesBtnCls,
                                )}
                            >
                                <div className="text-sm truncate max-w-[85px]">
                                    {showMatchupSection
                                        ? get( eventInfo, "matchups.0.abbr | guessList.0.yesDesc", "Yes" )
                                        : getOptionLabel( "yes", guess, eventInfo )}
                                </div>
                                <div>
                                    {formatUnitPrice( getOptionPrice( guess, "yesRealtimePrice" ), { precision: 1 } )}
                                </div>

                                {/*{calc( "yesRealtimePrice | !u:¢ =1", guess )}*/}
                            </button>
                            {/* 价格信息 */}
                            {/*<div className="text-center text-xs font-normal mt-2">*/}
                            {/*    <span className="text-t3">{calc( "yesRealtimePrice | !u:¢ =1", guess )}</span>*/}
                            {/*    <span className="mx-1 text-t3">→</span>*/}
                            {/*    <span className="text-action-accent">10¢</span>*/}
                            {/*</div>*/}
                        </div>

                        {/* 右边按钮 (Team 2) */}
                        <div className="flex-1" onClick={( e ) => e.stopPropagation()}>
                            <button
                                ref={( el ) => registerButton( "team2-button", el )}
                                onClick={() => handleMatchupClick( false )}
                                className={cn(
                                    "relative w-full h-10 rounded text-sm font-medium transition-opacity hover:opacity-80 flex items-center gap-1 justify-center",
                                    noBtnCls,
                                )}
                            >
                                <div className="text-sm truncate max-w-[85px]">
                                    {showMatchupSection
                                        ? get( eventInfo, "matchups.1.abbr | guessList.0.noDesc", "No" )
                                        : getOptionLabel( "no", guess, eventInfo )}
                                </div>
                                <div>
                                    {formatUnitPrice( getOptionPrice( guess, "noRealtimePrice" ), { precision: 1 } )}
                                </div>
                            </button>
                            {/* 价格信息 */}

                            {/*<div className="text-center text-xs font-normal mt-2">*/}
                            {/*    <span className="text-t3">{calc( "noRealtimePrice | =1 !u:¢", guess )}</span>*/}
                            {/*    <span className="mx-1 text-t3">→</span>*/}
                            {/*    <span className="text-action-accent">10¢</span>*/}
                            {/*</div>*/}
                        </div>
                    </div>
                )}
                <VolRect
                    eventInfo={eventInfo}
                    className="mt-3"
                    onCountdownEnd={() => {
                        setForceUpdate( ( prev ) => prev + 1 );
                    }}
                />
            </div>
        );
    },
);
